import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch } from "react-redux";
import { PageLayout } from "../../components/layout";
import { getKiosks } from "../../services/KioskService";
import { getAllCampaigns } from "../../services/CampaignService";
import { KioskConfigurationForm } from "../../components/KioskConfig/KioskConfigurationForm";
import CopyKioskConfigModal from "../../components/KioskConfig/CopyKioskConfigModal";
import { AppDispatch } from "../../state/store";
import { Kiosk } from "../../types/kiosk.type";
import { Campaign } from "../../types/campaign.types";
import { KioskConfiguration } from "../../types/kioskConfig.types";
import {
  getKioskConfiguration,
  listKioskConfigurationReviewRequests,
  reviewKioskConfigurationRequest,
} from "../../services/KioskConfigService";
import { showErrorToast } from "../../lib/ToastUtility";
import { useCurrentRole, useIsKioskOwner } from "../../hooks/useCurrentRole";
import { KioskConfigurationReviewRequest } from "../../types/kioskConfig.types";
import { AdminRole } from "../../types/constants.types";
import "./KioskConfigurationPage.css";

type DiffSectionKey = "Basic Info" | "Attract Screen" | "Donate Screen" | "Thanks Screen" | "Donation Amounts";

type DiffItem = {
  label: string;
  fieldKey: string;
  currentValue: string;
  newValue: string;
};

const LEGACY_FIELD_TO_TAB: Record<string, DiffSectionKey> = {
  campaignId: "Basic Info",
  partnerLogoUrl: "Basic Info",
  partnerName: "Basic Info",
  showPartnerBranding: "Basic Info",
  currencyCode: "Basic Info",
  currencySymbol: "Basic Info",
  currencyName: "Basic Info",
  isActive: "Basic Info",
  attractHeadline: "Attract Screen",
  attractHeadlineColor: "Attract Screen",
  attractDescription: "Attract Screen",
  attractDescriptionColor: "Attract Screen",
  attractBackgroundImageUrl: "Attract Screen",
  donateHeadline: "Donate Screen",
  donateHeadlineColor: "Donate Screen",
  donateDescription: "Donate Screen",
  donateDescriptionColor: "Donate Screen",
  donateBackgroundImageUrl: "Donate Screen",
  donateDailyGoal: "Donate Screen",
  thanksHeadline: "Thanks Screen",
  thanksHeadlineColor: "Thanks Screen",
  thanksDescription: "Thanks Screen",
  thanksDescriptionColor: "Thanks Screen",
  thanksBackgroundImageUrl: "Thanks Screen",
  donationAmounts: "Donation Amounts",
};

const TEXT_SETTINGS_PREFIX_BY_SECTION: Partial<Record<DiffSectionKey, "attract" | "donate" | "thanks">> = {
  "Attract Screen": "attract",
  "Donate Screen": "donate",
  "Thanks Screen": "thanks",
};

const DIFF_SECTION_FIELDS: Record<DiffSectionKey, Array<{ label: string; key: keyof KioskConfiguration }>> = {
  "Basic Info": [
    { label: "Campaign", key: "campaignId" },
    { label: "Partner Name", key: "partnerName" },
    { label: "Partner Logo URL", key: "partnerLogoUrl" },
    { label: "Partner Branding Enabled", key: "showPartnerBranding" },
    { label: "Currency Code", key: "currencyCode" },
    { label: "Currency Symbol", key: "currencySymbol" },
    { label: "Currency Name", key: "currencyName" },
  ],
  "Attract Screen": [
    { label: "Headline", key: "attractHeadline" },
    { label: "Headline Color", key: "attractHeadlineColor" },
    { label: "Description", key: "attractDescription" },
    { label: "Description Color", key: "attractDescriptionColor" },
    { label: "Background Image", key: "attractBackgroundImageUrl" },
  ],
  "Donate Screen": [
    { label: "Headline", key: "donateHeadline" },
    { label: "Headline Color", key: "donateHeadlineColor" },
    { label: "Description", key: "donateDescription" },
    { label: "Description Color", key: "donateDescriptionColor" },
    { label: "Background Image", key: "donateBackgroundImageUrl" },
    { label: "Daily Goal", key: "donateDailyGoal" },
  ],
  "Thanks Screen": [
    { label: "Headline", key: "thanksHeadline" },
    { label: "Headline Color", key: "thanksHeadlineColor" },
    { label: "Description", key: "thanksDescription" },
    { label: "Description Color", key: "thanksDescriptionColor" },
    { label: "Background Image", key: "thanksBackgroundImageUrl" },
  ],
  "Donation Amounts": [{ label: "Donation Tiers", key: "donationAmounts" }],
};

function humanizeTextSettingKey(key: string): string {
  const withoutPrefix = key.replace(/^(attract|donate|thanks)/, "");
  return withoutPrefix.replace(/([A-Z])/g, " $1").trim() || key;
}

function scopeTextSettingsByPrefix(settings: Record<string, string> | undefined, prefix: "attract" | "donate" | "thanks") {
  if (!settings) {
    return {};
  }

  return Object.entries(settings).reduce<Record<string, string>>((acc, [key, value]) => {
    if (key.startsWith(prefix)) {
      acc[key] = value;
    }
    return acc;
  }, {});
}

function ApprovalValueLabel({ text }: { text: string }) {
  return <label className="kiosk-approval-value-label">{text}</label>;
}

function formatDiffValue(value: unknown): string {
  if (value == null || value === "") {
    return "Not set";
  }

  if (typeof value === "boolean") {
    return value ? "Enabled" : "Disabled";
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return JSON.stringify(value, null, 2);
}

function getSectionFieldDiffs(section: DiffSectionKey, request: KioskConfigurationReviewRequest): DiffItem[] {
  const baseDiffs = DIFF_SECTION_FIELDS[section]
    .map((field) => {
      const currentValue = request.baseConfiguration?.[field.key];
      const newValue = request.proposedConfiguration?.[field.key];
      const changed = JSON.stringify(currentValue ?? null) !== JSON.stringify(newValue ?? null);

      return {
        label: field.label,
        fieldKey: String(field.key),
        currentValue: formatDiffValue(currentValue),
        newValue: formatDiffValue(newValue),
        changed,
      };
    })
    .filter((field) => field.changed);

  const textPrefix = TEXT_SETTINGS_PREFIX_BY_SECTION[section];
  if (!textPrefix) {
    return baseDiffs;
  }

  const currentSettings = scopeTextSettingsByPrefix(request.baseConfiguration?.textSettings, textPrefix);
  const newSettings = scopeTextSettingsByPrefix(request.proposedConfiguration?.textSettings, textPrefix);

  const settingsKeys = Array.from(new Set([...Object.keys(currentSettings), ...Object.keys(newSettings)]));

  const textDiffs = settingsKeys
    .filter((key) => (currentSettings[key] ?? "") !== (newSettings[key] ?? ""))
    .map((key) => ({
      label: humanizeTextSettingKey(key),
      fieldKey: `textSettings.${key}`,
      currentValue: formatDiffValue(currentSettings[key]),
      newValue: formatDiffValue(newSettings[key]),
    }));

  return [...baseDiffs, ...textDiffs];
}

function getRequestDiffGroups(request: KioskConfigurationReviewRequest) {
  const tabs = normalizeChangedTabs(request.changedFields || []);

  return tabs
    .map((section) => ({
      section,
      diffs: getSectionFieldDiffs(section, request),
    }))
    .filter((group) => group.diffs.length > 0);
}

function getChangedTabsFromRequest(request: KioskConfigurationReviewRequest | null): DiffSectionKey[] {
  if (!request) {
    return [];
  }

  return getRequestDiffGroups(request).map((group) => group.section);
}

function getReviewBadgeLabel(status: KioskConfigurationReviewRequest["status"]): string {
  switch (status) {
    case "PENDING":
      return "Config review pending";
    case "APPROVED":
      return "Config approved";
    case "REJECTED":
      return "Config rejected";
    default:
      return "Config review";
  }
}

function formatReviewTimestamp(value?: string): string {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatChangedFieldLabel(field: string): string {
  if (!field) {
    return "Unknown field";
  }

  if (field === "Basic Info" || field === "Attract Screen" || field === "Donate Screen" || field === "Thanks Screen" || field === "Donation Amounts") {
    return field;
  }

  if (field.startsWith("textSettings.")) {
    const key = field.replace(/^textSettings\./, "");
    return `${humanizeTextSettingKey(key)} (${normalizeChangedTabs([field])[0] || "Tab detail"})`;
  }

  if (field.startsWith("attract")) {
    return `Attract Screen · ${humanizeTextSettingKey(field.replace(/^attract/, ""))}`;
  }

  if (field.startsWith("donate")) {
    return `Donate Screen · ${humanizeTextSettingKey(field.replace(/^donate/, ""))}`;
  }

  if (field.startsWith("thanks")) {
    return `Thanks Screen · ${humanizeTextSettingKey(field.replace(/^thanks/, ""))}`;
  }

  const mappedTab = LEGACY_FIELD_TO_TAB[field];
  if (mappedTab) {
    return `${mappedTab} · ${field}`;
  }

  return field;
}

function getReviewFieldHistoryEntries(request: KioskConfigurationReviewRequest) {
  return getRequestDiffGroups(request);
}

function normalizeChangedTabs(changedFields: string[]): DiffSectionKey[] {
  const uniqueTabs = new Set<DiffSectionKey>();

  changedFields.forEach((field) => {
    if (
      field === "Basic Info" ||
      field === "Attract Screen" ||
      field === "Donate Screen" ||
      field === "Thanks Screen" ||
      field === "Donation Amounts"
    ) {
      uniqueTabs.add(field);
      return;
    }

    if (field.startsWith("attract") || field.startsWith("textSettings.attract")) {
      uniqueTabs.add("Attract Screen");
      return;
    }

    if (field.startsWith("donate") || field.startsWith("textSettings.donate")) {
      uniqueTabs.add("Donate Screen");
      return;
    }

    if (field.startsWith("thanks") || field.startsWith("textSettings.thanks")) {
      uniqueTabs.add("Thanks Screen");
      return;
    }

    const mappedTab = LEGACY_FIELD_TO_TAB[field];
    if (mappedTab) {
      uniqueTabs.add(mappedTab);
    }
  });

  return Array.from(uniqueTabs);
}

const KioskConfigurationPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isKioskOwner = useIsKioskOwner();
  const currentRole = useCurrentRole();
  const isAdminOrManager =
    currentRole?.toUpperCase() === AdminRole.ADMIN ||
    currentRole?.toUpperCase() === AdminRole.T2F_MANAGER;
  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedKiosk, setSelectedKiosk] = useState<Kiosk | null>(null);
  const [loadingKiosks, setLoadingKiosks] = useState(true);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [savedConfig, setSavedConfig] = useState<KioskConfiguration | null>(null);
  const [copySourceKiosk, setCopySourceKiosk] = useState<Kiosk | null>(null);
  const [copySourceConfig, setCopySourceConfig] = useState<KioskConfiguration | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [partnerLogoByKioskId, setPartnerLogoByKioskId] = useState<Record<string, string>>({});
  const [hiddenLogoKioskIds, setHiddenLogoKioskIds] = useState<Record<string, boolean>>({});
  const [reviewRequestsByKioskId, setReviewRequestsByKioskId] = useState<Record<string, KioskConfigurationReviewRequest[]>>({});
  const [loadingReviewRequests, setLoadingReviewRequests] = useState(false);
  const [reviewActionLoadingId, setReviewActionLoadingId] = useState<number | null>(null);
  const [reviewCommentByRequestId, setReviewCommentByRequestId] = useState<Record<number, string>>({});
  const [selectedFieldsByRequestId, setSelectedFieldsByRequestId] = useState<Record<number, string[]>>({});
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [showReviewWorkflowDialog, setShowReviewWorkflowDialog] = useState(false);

  const filteredKiosks = kiosks.filter((k) => {
    if (showPendingOnly) {
      const pendingItems = (reviewRequestsByKioskId[k.kioskId] || []).filter((request) => request.status === "PENDING");
      if (pendingItems.length === 0) {
        return false;
      }
    }

    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (k.name || "").toLowerCase().includes(q) ||
      (k.kioskId || "").toLowerCase().includes(q) ||
      (k.location || "").toLowerCase().includes(q) ||
      (k.region || "").toLowerCase().includes(q) ||
      (k.description || "").toLowerCase().includes(q) ||
      (k.ownerEmail || "").toLowerCase().includes(q) ||
      (k.kioskType || "").toLowerCase().includes(q) ||
      (k.active ? "active" : "inactive").includes(q)
    );
  });

  const pendingReviewCount = Object.values(reviewRequestsByKioskId)
    .flat()
    .filter((request) => request.status === "PENDING").length;

  const getLatestReviewRequest = (kioskId: string): KioskConfigurationReviewRequest | null => {
    const requests = reviewRequestsByKioskId[kioskId] || [];
    if (requests.length === 0) return null;
    return [...requests].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];
  };

  const selectedKioskReviewRequests = selectedKiosk
    ? [...(reviewRequestsByKioskId[selectedKiosk.kioskId] || [])].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
    : [];

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingKiosks(true);
        const kioskList = await getKiosks();
        setKiosks(kioskList);
      } catch (error) {
        showErrorToast((error as Error).message || "Failed to load kiosks");
      } finally {
        setLoadingKiosks(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        setLoadingCampaigns(true);
        const response = await dispatch(getAllCampaigns({}));
        if (getAllCampaigns.fulfilled.match(response) && Array.isArray(response.payload)) {
          setCampaigns(response.payload);
        }
      } catch (error) {
        showErrorToast((error as Error).message || "Failed to load campaigns");
      } finally {
        setLoadingCampaigns(false);
      }
    };

    loadCampaigns();
  }, [dispatch]);

  useEffect(() => {
    if (kiosks.length === 0) {
      setPartnerLogoByKioskId({});
      setHiddenLogoKioskIds({});
      return;
    }

    let isMounted = true;

    const loadPartnerLogos = async () => {
      const results = await Promise.allSettled(
        kiosks.map(async (kiosk) => {
          try {
            const config = await getKioskConfiguration(kiosk.kioskId);
            const partnerLogoUrl = config.partnerLogoUrl?.trim();
            if (!partnerLogoUrl) {
              return null;
            }

            return {
              kioskId: kiosk.kioskId,
              partnerLogoUrl,
            };
          } catch {
            return null;
          }
        }),
      );

      if (!isMounted) {
        return;
      }

      const nextLogos: Record<string, string> = {};
      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value?.partnerLogoUrl) {
          nextLogos[result.value.kioskId] = result.value.partnerLogoUrl;
        }
      });

      setPartnerLogoByKioskId(nextLogos);
      setHiddenLogoKioskIds({});
    };

    loadPartnerLogos();

    return () => {
      isMounted = false;
    };
  }, [kiosks]);

  const loadReviewRequests = async (kioskId?: string) => {
    setLoadingReviewRequests(true);
    try {
      const requests = await listKioskConfigurationReviewRequests({
        status: showPendingOnly ? "PENDING" : undefined,
        kioskId,
      });
      const grouped = requests.reduce<Record<string, KioskConfigurationReviewRequest[]>>((acc, request) => {
        const key = request.kioskPublicId;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(request);
        return acc;
      }, {});

      if (kioskId) {
        setReviewRequestsByKioskId((prev) => ({
          ...prev,
          ...grouped,
        }));
      } else {
        setReviewRequestsByKioskId(grouped);
      }
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to load CMS review requests");
    } finally {
      setLoadingReviewRequests(false);
    }
  };

  useEffect(() => {
    loadReviewRequests();
  }, [showPendingOnly]);

  useEffect(() => {
    if (!selectedKiosk) {
      return;
    }

    loadReviewRequests(selectedKiosk.kioskId);
  }, [selectedKiosk?.kioskId]);

  const handleKioskSelect = (kiosk: Kiosk) => {
    setSelectedKiosk(kiosk);
    setSavedConfig(null);
  };

  const handleConfigSaved = (config: KioskConfiguration) => {
    setSavedConfig(config);
    loadReviewRequests();
  };

  const handleReviewSubmissionSuccess = () => {
    setSavedConfig(null);
    loadReviewRequests();
  };

  const getLatestPendingReviewRequest = (kioskId: string): KioskConfigurationReviewRequest | null => {
    const requests = (reviewRequestsByKioskId[kioskId] || [])
      .filter((request) => request.status === "PENDING")
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return requests[0] || null;
  };

  const handleReviewAction = async (
    request: KioskConfigurationReviewRequest,
    action: "APPROVE" | "REJECT",
    approvedFields?: string[],
    rejectedFields?: string[],
    explicitComment?: string,
  ) => {
    setReviewActionLoadingId(request.id);
    try {
      await reviewKioskConfigurationRequest(request.id, {
        action,
        comment: explicitComment?.trim() || reviewCommentByRequestId[request.id]?.trim() || undefined,
        approvedFields,
        rejectedFields,
      });
      await loadReviewRequests();
      setReviewCommentByRequestId((prev) => {
        const next = { ...prev };
        delete next[request.id];
        return next;
      });

      if (selectedKiosk) {
        try {
          const config = await getKioskConfiguration(selectedKiosk.kioskId);
          setSavedConfig(config);
        } catch {
          // no-op
        }
      }
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to process review action");
    } finally {
      setReviewActionLoadingId(null);
    }
  };

  const handleBackToList = () => {
    setSelectedKiosk(null);
    setSavedConfig(null);
    setShowReviewWorkflowDialog(false);
  };

  const handleOpenCopyModal = async (kiosk: Kiosk, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isKioskOwner) {
      return;
    }
    setCopySourceKiosk(kiosk);
    setCopySourceConfig(null);
    try {
      const config = await getKioskConfiguration(kiosk.kioskId);
      setCopySourceConfig(config);
    } catch {
      // config may not exist yet; modal still opens but without preview chips
    }
  };

  const handleCloseCopyModal = () => {
    setCopySourceKiosk(null);
    setCopySourceConfig(null);
  };

  const handleApprovePendingChanges = async (approvedFields?: string[]) => {
    if (!selectedKiosk) {
      return;
    }

    const pendingRequest = getLatestPendingReviewRequest(selectedKiosk.kioskId);
    if (!pendingRequest) {
      return;
    }

    const dedupedFields = approvedFields && approvedFields.length > 0
      ? Array.from(new Set(approvedFields))
      : undefined;

    await handleReviewAction(pendingRequest, "APPROVE", dedupedFields);
  };

  const handleRejectPendingChanges = async (reviewComment: string, rejectedFields?: string[]) => {
    if (!selectedKiosk) {
      return;
    }

    const pendingRequest = getLatestPendingReviewRequest(selectedKiosk.kioskId);
    if (!pendingRequest) {
      return;
    }

    const dedupedRejectedFields = rejectedFields && rejectedFields.length > 0
      ? Array.from(new Set(rejectedFields))
      : undefined;

    await handleReviewAction(pendingRequest, "REJECT", undefined, dedupedRejectedFields, reviewComment);
  };

  const getSelectedFieldsForRequest = (requestId: number) => selectedFieldsByRequestId[requestId] || [];

  const toggleFieldSelection = (requestId: number, fieldKey: string) => {
    setSelectedFieldsByRequestId((prev) => {
      const selected = prev[requestId] || [];
      const nextSelected = selected.includes(fieldKey)
        ? selected.filter((key) => key !== fieldKey)
        : [...selected, fieldKey];

      return {
        ...prev,
        [requestId]: nextSelected,
      };
    });
  };

  const selectAllRequestFields = (requestId: number, fieldKeys: string[]) => {
    setSelectedFieldsByRequestId((prev) => ({
      ...prev,
      [requestId]: Array.from(new Set(fieldKeys)),
    }));
  };

  const clearSelectedRequestFields = (requestId: number) => {
    setSelectedFieldsByRequestId((prev) => ({
      ...prev,
      [requestId]: [],
    }));
  };

  const activePendingRequest = selectedKiosk
    ? getLatestPendingReviewRequest(selectedKiosk.kioskId)
    : null;

  return (
    <PageLayout
      title={selectedKiosk ? `${selectedKiosk.name || selectedKiosk.kioskId} - KIOSK CMS Configuration` : "KIOSK CMS Configuration"}
      fillHeight={Boolean(selectedKiosk)}
      className={isKioskOwner && selectedKiosk ? "owner-kiosk-config-editing" : undefined}
    >
      <div className={`kiosk-configuration-page${selectedKiosk ? " editing" : ""}`}>
        {!selectedKiosk ? (
          <>
            <div className="list-toolbar">
              <div className="list-toolbar-left">
                <p className="subtitle">Manage screens, branding, donation tiers, and owner settings</p>
                {isAdminOrManager && (
                  <>
                    <span className={`review-pill ${pendingReviewCount > 0 ? "pending" : "clear"}`}>
                      {pendingReviewCount > 0 ? `Needs Review: ${pendingReviewCount}` : "Review Queue Clear"}
                    </span>
                    <label className="pending-filter-toggle">
                      <input
                        type="checkbox"
                        checked={showPendingOnly}
                        onChange={(event) => setShowPendingOnly(event.target.checked)}
                      />
                      <span>Pending only</span>
                    </label>
                  </>
                )}
                {!loadingKiosks && kiosks.length > 0 && (
                  <span className="kiosk-count-badge">
                    {filteredKiosks.length} / {kiosks.length} kiosk{kiosks.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
              {!loadingKiosks && kiosks.length > 0 && (
                <div className="kiosk-search-bar">
                  <svg className="search-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.75"/>
                    <path d="M13 13L17 17" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
                  </svg>
                  <input
                    type="text"
                    className="kiosk-search-input"
                    placeholder="Search by name, ID, location, region, type, owner…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search kiosks"
                  />
                  {searchQuery && (
                    <button className="search-clear-btn" onClick={() => setSearchQuery("")} aria-label="Clear search">✕</button>
                  )}
                </div>
              )}
            </div>
          <div className="kiosk-list-container">

            {loadingKiosks ? (
              <div className="loading-state">
                <div className="loading-spinner" />
                <span>Loading kiosks…</span>
              </div>
            ) : kiosks.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📟</div>
                <p className="empty-state-title">No kiosks yet</p>
                <p className="empty-state-sub">Create a kiosk first to configure it here.</p>
              </div>
            ) : filteredKiosks.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🔍</div>
                <p className="empty-state-title">No matches found</p>
                <p className="empty-state-sub">Try a different name, ID, location, or status.</p>
              </div>
            ) : (
              <div className="kiosk-grid">
                {filteredKiosks.map((kiosk) => {
                  const partnerLogoUrl = partnerLogoByKioskId[kiosk.kioskId];
                  const showPartnerLogo = Boolean(partnerLogoUrl) && !hiddenLogoKioskIds[kiosk.kioskId];
                  const latestReview = getLatestReviewRequest(kiosk.kioskId);
                  const reviewStatusClass = latestReview
                    ? latestReview.status === "PENDING"
                      ? "review-status-pending"
                      : latestReview.status === "APPROVED"
                      ? "review-status-approved"
                      : "review-status-rejected"
                    : "";

                  return (
                  <div
                    key={kiosk.id}
                    className={`kiosk-card ${kiosk.active ? "active" : "inactive"}`}
                    onClick={() => handleKioskSelect(kiosk)}
                  >
                    <div className="kiosk-card-header">
                      <div className="kiosk-card-header-left">
                        <div className="kiosk-avatar">
                          {(kiosk.name || kiosk.kioskId).charAt(0).toUpperCase()}
                        </div>
                        <div className="kiosk-card-title-group">
                          <h3 className="kiosk-card-name">{kiosk.name || kiosk.kioskId}</h3>
                          <code className="kiosk-card-id">{kiosk.kioskId}</code>
                        </div>
                      </div>
                      <div className="kiosk-card-header-right">
                        {latestReview && (
                          <span className={`review-status-badge ${reviewStatusClass}`}>
                            {getReviewBadgeLabel(latestReview.status)}
                          </span>
                        )}
                        {showPartnerLogo && (
                          <img
                            src={partnerLogoUrl}
                            alt="Partner logo"
                            className="partner-logo-thumb"
                            loading="lazy"
                            onError={() =>
                              setHiddenLogoKioskIds((prev) => ({
                                ...prev,
                                [kiosk.kioskId]: true,
                              }))
                            }
                          />
                        )}
                        <span className={`status-badge ${kiosk.active ? "active" : "inactive"}`}>
                          <span className="status-dot" />
                          {kiosk.active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>

                    <div className="kiosk-card-body">
                      {kiosk.location && (
                        <div className="kiosk-meta-row">
                          <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 1.5C5.51 1.5 3.5 3.51 3.5 6c0 3.5 4.5 8.5 4.5 8.5s4.5-5 4.5-8.5c0-2.49-2.01-4.5-4.5-4.5zm0 6a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" fill="currentColor"/></svg>
                          <span>{kiosk.location}{kiosk.region ? `, ${kiosk.region}` : ""}</span>
                        </div>
                      )}
                      {kiosk.kioskType && (
                        <div className="kiosk-meta-row">
                          <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="3" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M5 14h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                          <span>{kiosk.kioskType}</span>
                        </div>
                      )}
                      {kiosk.ownerEmail && (
                        <div className="kiosk-meta-row">
                          <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M2.5 13.5c0-2.485 2.462-4.5 5.5-4.5s5.5 2.015 5.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                          <span>{kiosk.ownerEmail}</span>
                        </div>
                      )}
                    </div>

                    <div className="kiosk-card-footer">
                      <button className="configure-button">
                        Configure
                        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                      {!isKioskOwner && kiosks.length > 1 && (
                        <button
                          className="copy-config-trigger-btn"
                          onClick={(e) => handleOpenCopyModal(kiosk, e)}
                          title="Copy configuration to other kiosks"
                          aria-label="Copy configuration"
                        >
                          <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="5" y="5" width="8" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M3 11V3a1 1 0 0 1 1-1h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                          Copy Config
                        </button>
                      )}
                    </div>
                  </div>
                );
                })}
              </div>
            )}
          </div>
          </>
        ) : (
          <div className="kiosk-config-container">
            <div className="kiosk-config-toolbar">
              <div className="kiosk-config-toolbar-left">
                <button type="button" className="kiosk-back-button" onClick={handleBackToList}>
                  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Back to Kiosk List
                </button>
                <button
                  type="button"
                  className="kiosk-review-history-button"
                  onClick={() => setShowReviewWorkflowDialog(true)}
                >
                  Review History
                </button>
              </div>
              <span className="kiosk-config-toolbar-title">
                {selectedKiosk.name || selectedKiosk.kioskId}
              </span>
            </div>
            {loadingCampaigns ? (
              <div className="loading-state">Loading campaigns...</div>
            ) : campaigns.length === 0 ? (
              <div className="empty-state">
                <p>No campaigns available. Create a campaign first to configure this kiosk.</p>
              </div>
            ) : (
              <>
                {savedConfig && (
                  <div className="success-message">
                    <p>✓ Configuration saved successfully for {selectedKiosk.name || selectedKiosk.kioskId}</p>
                  </div>
                )}
                <KioskConfigurationForm
                  kiosk={selectedKiosk}
                  campaigns={campaigns}
                  onSaved={handleConfigSaved}
                  restrictToAmountsOnly={false}
                  moderatedSubmission={isKioskOwner}
                  onReviewSubmissionSuccess={handleReviewSubmissionSuccess}
                  highlightedTabLabels={
                    isAdminOrManager
                      ? getChangedTabsFromRequest(getLatestPendingReviewRequest(selectedKiosk.kioskId))
                      : []
                  }
                  pendingReviewRequest={isAdminOrManager ? activePendingRequest : null}
                  onApprovePendingChanges={isAdminOrManager ? handleApprovePendingChanges : undefined}
                  onRejectPendingChanges={isAdminOrManager ? handleRejectPendingChanges : undefined}
                  approvalLoading={Boolean(activePendingRequest && reviewActionLoadingId === activePendingRequest.id)}
                  reloadToken={savedConfig?.updatedAt || ""}
                />
              </>
            )}
          </div>
        )}
      </div>
      {showReviewWorkflowDialog && selectedKiosk &&
        ReactDOM.createPortal(
          <div className="kiosk-review-history-backdrop" onClick={() => setShowReviewWorkflowDialog(false)}>
            <div className="kiosk-review-history-modal" onClick={(event) => event.stopPropagation()}>
              <div className="kiosk-review-history-header">
                <div>
                  <p className="kiosk-review-history-kicker">Change log</p>
                  <h3>{selectedKiosk.name || selectedKiosk.kioskId}</h3>
                </div>
                <button
                  type="button"
                  className="kiosk-review-history-close"
                  onClick={() => setShowReviewWorkflowDialog(false)}
                  aria-label="Close review history"
                >
                  ✕
                </button>
              </div>

              {selectedKioskReviewRequests.length === 0 ? (
                <div className="kiosk-review-history-empty">
                  <p>No review activity has been recorded for this kiosk yet.</p>
                </div>
              ) : (
                <div className="kiosk-review-history-list">
                  {selectedKioskReviewRequests.map((request) => {
                    const reviewDiffGroups = getReviewFieldHistoryEntries(request);

                    return (
                      <div key={request.id} className="kiosk-review-history-item">
                        <div className="kiosk-review-history-item-header">
                          <span className={`review-status-badge ${request.status === "PENDING" ? "review-status-pending" : request.status === "APPROVED" ? "review-status-approved" : "review-status-rejected"}`}>
                            {request.status}
                          </span>
                          <time>{formatReviewTimestamp(request.reviewedAt || request.createdAt)}</time>
                        </div>

                        <div className="kiosk-review-history-meta">
                          <p>
                            <strong>Requested by:</strong> {request.requestedByEmail || "Unknown"}
                          </p>
                          <p>
                            <strong>Reviewed by:</strong> {request.reviewedByEmail || request.requestedByEmail || "Pending"}
                          </p>
                        </div>

                        {request.submissionComment && (
                          <div className="kiosk-review-history-note">
                            <strong>Submission comment:</strong>
                            <p>{request.submissionComment}</p>
                          </div>
                        )}

                        {request.reviewComment && (
                          <div className="kiosk-review-history-note">
                            <strong>Review comment:</strong>
                            <p>{request.reviewComment}</p>
                          </div>
                        )}

                        {reviewDiffGroups.length > 0 && (
                          <div className="kiosk-review-history-field-list">
                            <strong>Changed tab / field details:</strong>
                            <div className="kiosk-review-history-diff-groups">
                              {reviewDiffGroups.map((group) => (
                                <div key={group.section} className="kiosk-review-history-diff-group">
                                  <div className="kiosk-review-history-diff-group-title">{group.section}</div>
                                  <div className="kiosk-review-history-diff-rows">
                                    {group.diffs.map((diff) => (
                                      <div key={`${group.section}-${diff.fieldKey}`} className="kiosk-review-history-diff-row">
                                        <div className="kiosk-review-history-diff-label">{diff.label}</div>
                                        <div className="kiosk-review-history-diff-values">
                                          <div>
                                            <span>Old:</span>
                                            <p>{diff.currentValue}</p>
                                          </div>
                                          <div>
                                            <span>New:</span>
                                            <p>{diff.newValue}</p>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
      {copySourceKiosk && (
        <CopyKioskConfigModal
          open={Boolean(copySourceKiosk)}
          sourceKiosk={copySourceKiosk}
          sourceConfig={copySourceConfig}
          allKiosks={kiosks}
          onClose={handleCloseCopyModal}
        />
      )}
    </PageLayout>
  );
};

export default KioskConfigurationPage;
